import store from 'redux/store'
import orgamax from 'ampersand-app'
import { updateFileInfo } from './list'
import { fileMetaKeys } from '@/modules/file/fileService'
import { isEqual, omit } from 'lodash'
import { modalConfirmation } from '@/modules/_helpers/openModal'
import { experimentalFeatures } from '@/modules/base/util/constants'
import lang from '@/lang'
import {
  fetchDocumentDetails,
  setAnalysisDone,
  setIsSaving,
  bulkUpdateDocument,
  updateItem,
  fetchDrawerPage,
  setDrawerDocument,
} from './drawer'
import { fetchListAction } from '../lists/requests/documentList'
import { QUERY_TYPES } from '@/redux/ducks/lists'
import { toast } from '@/modules/base/components/toast/toastConfig'
import { OCR_FIELD_MAPPING } from '@/modules/document/constants/ocrFieldMapping'
import { isEmbeddedInvoiceReview } from '@/modules/document/utils/invoice-analysis-state'
import { buildArtChangeReviewClosure } from '@/modules/document/utils/review-art-change'
import { DESKTOP_EDITABLE_FILE_FIELDS, isDesktopOwnedFile, pickDesktopEditableFileFields } from '@/modules/file/desktop-owned-file'

export const saveDocument = (callback, prompt = false) => {
  const { initialDocument, document: currentDocument, folderReadOnly } = store.getState().document.drawer

  // FLAG:useDocuAi — for a document whose Details tab hosts the central review, documentInvoiceDetails
  // belongs to THAT review alone. The generic drawer save must not carry it: the server reads a
  // documentInvoiceDetails-carrying PUT on a still-pending row as the review confirmation and books
  // the Ausgabe/Einnahme from it (fileService/update.js, `pendingReview`). So a plain „Speichern" on
  // the Allgemein tab — or the bank-data prefill dirtying a single field — created an expense the
  // operator never asked for. Only the review's own Save (save-document-invoice-review.ts, marked
  // with saveReviewPayee) may complete a review, and only with the booking target picked there.
  const useDocuAiEnabled = !!orgamax.user?.experimentalFeatures?.includes(experimentalFeatures.useDocuAi)
  const reviewOwnsInvoiceDetails = useDocuAiEnabled && isEmbeddedInvoiceReview(currentDocument, folderReadOnly)

  // A file orgaMAX Desktop owns can only ever save notes and tags, so everything else is not just
  // dropped from the payload below — it must not count as a change either, or the drawer would
  // announce „Speichern" and fire a PUT that changes nothing (see desktop-owned-file.ts).
  const desktopOwned = isDesktopOwnedFile(currentDocument)

  // FLAG:useDocuAi — the one exception to the rule above: when the „Art" is reclassified to
  // something nobody reviews (Rechnungsausgang, Sonstiges, Vertrag …), this save closes the open
  // review as a document-only review — otherwise the row would sit on „Zu prüfen" forever although
  // the new Art offers no review entry point any more. See review-art-change.ts;
  // `bookingTarget: 'document'` keeps that closure from booking anything.
  const artChangeReviewClosure = useDocuAiEnabled
    ? buildArtChangeReviewClosure(currentDocument, initialDocument)
    : null

  // Confirming the extracted OCR values INTO documentInvoiceDetails is the legacy details tab's
  // save. While the review owns those fields it must not run either — it would dirty the document
  // and post exactly the payload above.
  if (!reviewOwnsInvoiceDetails && !desktopOwned) {
    const validUpdates = Object.entries(OCR_FIELD_MAPPING)
      .map(([key, ocrField]) => ({
        key,
        value: currentDocument.documentInvoiceDetails?.[key.split('.').pop()] ?? currentDocument.metaData?.ocr?.[ocrField],
      }))
      .filter(({ value }) => value != null && value !== '' && value !== 0)

    if (validUpdates.length > 0) {
      store.dispatch(bulkUpdateDocument({ updates: validUpdates }))
    }
  }

  const { document } = store.getState().document.drawer
  let change = false

  // Check for changes in file meta keys
  for (const key of fileMetaKeys) {
    if (desktopOwned && !DESKTOP_EDITABLE_FILE_FIELDS.includes(key)) continue
    if (reviewOwnsInvoiceDetails && key === 'documentInvoiceDetails') continue
    if (!isEqual(document[key], initialDocument[key])) {
      change = true
      break
    }
  }

  // Check for changes in documentInvoiceDetails
  if (!change && !reviewOwnsInvoiceDetails && !desktopOwned && document.documentInvoiceDetails && !isEqual(document.documentInvoiceDetails, initialDocument.documentInvoiceDetails)) {
    change = true
  }

  // Check for changes in metaData.ocr
  if (!change && !desktopOwned && document.metaData?.ocr && !isEqual(document.metaData.ocr, initialDocument.metaData?.ocr)) {
    change = true
  }

  if (!change && !artChangeReviewClosure) {
    return callback()
  }

  // The store keeps the full document (the review reads it); only the PUT drops the review-owned
  // field, so a rename/tag/note save stays a rename/tag/note save on the server too.
  //
  // A desktop-owned file is the narrowest case and therefore decided first: the API refuses the
  // whole request over any key beyond notes/tags, so posting the full document refused the note the
  // user had just typed along with it. Everything else on such a file is read-only in the drawer
  // (see desktop-owned-file.ts), so nothing is lost by leaving it out.
  const payload = desktopOwned
    ? pickDesktopEditableFileFields(document)
    : artChangeReviewClosure
      ? { ...document, documentInvoiceDetails: artChangeReviewClosure }
      : reviewOwnsInvoiceDetails ? omit(document, ['documentInvoiceDetails']) : document

  const save = () => {
    store.dispatch(setIsSaving(true))
    store.dispatch(updateFileInfo(payload, initialDocument, () => {
      store.dispatch(setAnalysisDone(false))
      store.dispatch(updateItem(document))
      store.dispatch(fetchListAction({ queryType: QUERY_TYPES.COUNTER }))
      callback()
    }))
      .finally(() => {
        store.dispatch(setIsSaving(false))
      })
  }

  if (!prompt) {
    return save()
  }

  modalConfirmation(save, {
    title: lang.documents.navigationConfirmation.title,
    description: lang.documents.navigationConfirmation.description,
    confirmButtonLabel: lang.generics.save,
    abortButtonLabel: lang.generics.discard,
    onAbort: callback,
  })
}

interface DocumentDrawerParam {
  documentId: string,
  analyze?: boolean,
  rename?: boolean,
  rowIndex?: number,
  // FLAG:useDocuAi — matched-child preselect for the preview switcher (search rollup).
  preselectChildVersionId?: string | null,
  // Receipt-list terminal rows are not part of the Documents list, so there are no neighboring
  // drawer pages to load. The file itself still opens through fetchDocumentDetails.
  withListNavigation?: boolean,
}

export const openDocumentDrawer = async(action: DocumentDrawerParam) => {
  const { analyze, rename, documentId, rowIndex, preselectChildVersionId, withListNavigation = true } = action
  await store.dispatch(fetchDocumentDetails({
    documentId,
    analyze,
    rename,
    analysisDone: !analyze,
    analysisActive: !!analyze,
    rowIndex,
    preselectChildVersionId,
  }))

  if (withListNavigation) {
    store.dispatch(fetchDrawerPage({ step: 0, documentId }))
  }
}

export const documentDrawerNavigate = (step) => {
  const { page: { next, previous } } = store.getState().document.drawer

  const document = step === 1 ? next : previous
  if (!document) {
    return
  }
  store.dispatch(setDrawerDocument(document))
  store.dispatch(fetchDrawerPage({ step, documentId: document.id }))
}

export const handleApiError = (e, message = lang.documents.errors.default, silent = false) => {
  if (silent) {
    return console.error(e)
  }

  if (!e?.meta) {
    toast.showToast({ type: 'error', message })
  } else {
    // ToDo: handle errors specifically
    toast.showToast({ type: 'error', message })
  }
}
